import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { cssMap as cssMapUnbounded } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import CrossIcon from '@atlaskit/icon/core/cross';
import IconButton from '@atlaskit/button/icon/button';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image/image';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	useRouteName,
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	VIEW_PAGE,
	VIEW_BLOG,
	VIEW_BLOG_DATE_LEGACY,
} from '@confluence/named-routes/entry-points/routes.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import type { BANNER_HEIGHT } from './BannerSize.ts';
import { cwrWelcomeBannerMessages as i18n } from './messages.ts';
import LightWelcomeImage from '../../assets/LightWelcomeImage.png';
import DarkWelcomeImage from '../../assets/DarkWelcomeImage.png';

export const CWR_WELCOME_QUERY_PARAM = 'cwrWelcome';

export type CwrWelcomeVariant = 'personalSpace' | 'sharedSpace' | 'liveDoc';

const CWR_WELCOME_VARIANTS: readonly CwrWelcomeVariant[] = [
	'personalSpace',
	'sharedSpace',
	'liveDoc',
];

const isCwrWelcomeVariant = (value: string | undefined): value is CwrWelcomeVariant =>
	!!value && (CWR_WELCOME_VARIANTS as readonly string[]).includes(value);

const useCwrWelcomeParam = createSingleQueryParamHook(CWR_WELCOME_QUERY_PARAM);

const unboundedStyles = cssMapUnbounded({
	root: {
		// classic pages are 100% + 80px but we have to fill the container's left margin
		width: 'calc(100% + var(--full-cover-image-margin, 0)/2 )',
		marginLeft: 'var(--full-cover-image-margin, 0px)',
		paddingInline: token('space.600'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('color.background.accent.blue.subtlest'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		height: `${130 satisfies typeof BANNER_HEIGHT}px`,
		position: 'relative',
		overflow: 'hidden',
	},
	headingStyles: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitLineClamp: 1,
		WebkitBoxOrient: 'vertical',
	},
	subtextStyles: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
	},
	livePageStyle: {
		marginLeft: token('space.0'),
		// ensures padding is subtracted from the inside for live pages
		width: 'auto',
		borderTop: 'none',
	},
});

const styles = cssMap({
	contentContainerStyles: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		gap: token('space.100'),
		zIndex: 1,
		padding: token('space.300'),
		width: '60%',
		overflow: 'hidden',
	},
	svgContainer: {
		width: '40%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
	closeButton: {
		position: 'absolute',
		top: token('space.150'),
		right: token('space.150'),
		pointerEvents: 'auto',
	},
});

const SUBTEXT_BY_VARIANT: Record<CwrWelcomeVariant, (typeof i18n)[keyof typeof i18n]> = {
	personalSpace: i18n.bannerPersonalSpaceSubtext,
	sharedSpace: i18n.bannerSharedSpaceSubtext,
	liveDoc: i18n.bannerLiveDocSubtext,
};

type CwrWelcomeBannerProps = {
	contentId: string;
	isLivePage: boolean;
};

const CwrWelcomeBannerContent = ({
	isLivePage,
	variant,
}: {
	isLivePage: boolean;
	variant: CwrWelcomeVariant;
}) => {
	const [isDismissed, setIsDismissed] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { displayName } = useSessionData();
	const { setQueryParams } = useRouteActions();
	const { formatMessage } = useIntl();

	const firstName = displayName?.split(' ')[0] ?? null;

	useEffect(() => {
		if (!isDismissed) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'cwrWelcomeBanner',
					attributes: {
						variant,
					},
				},
			}).fire();
		}
		// `variant` is stable per-mount; omitted to avoid a redundant re-fire.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDismissed, createAnalyticsEvent]);

	const handleDismiss = () => {
		setIsDismissed(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'banner',
				actionSubjectId: 'cwrWelcomeBanner',
				source: 'viewPageContentContainer',
				attributes: {
					variant,
				},
			},
		}).fire();
		setQueryParams({ [CWR_WELCOME_QUERY_PARAM]: undefined });
	};

	if (isDismissed) {
		return null;
	}

	const subtextMessage = SUBTEXT_BY_VARIANT[variant];

	return (
		<div
			data-vc="cwr-welcome-banner"
			css={[unboundedStyles.root, isLivePage && unboundedStyles.livePageStyle]}
		>
			<Flex alignItems="center">
				<Box xcss={cx(styles.contentContainerStyles)}>
					<Box xcss={unboundedStyles.headingStyles}>
						<Heading size="large">
							{firstName ? (
								<FormattedMessage {...i18n.bannerWelcomeMessageWithName} values={{ firstName }} />
							) : (
								<FormattedMessage {...i18n.bannerWelcomeMessage} />
							)}
						</Heading>
					</Box>
					<Box xcss={unboundedStyles.subtextStyles}>
						<Text as="p">
							<FormattedMessage {...subtextMessage} />
						</Text>
					</Box>
				</Box>
				<Box xcss={cx(styles.svgContainer)}>
					<Image src={LightWelcomeImage} srcDark={DarkWelcomeImage} alt="" />
				</Box>
			</Flex>
			<Box xcss={cx(styles.closeButton)}>
				<IconButton
					testId="cwr-welcome-banner-button-close"
					appearance="subtle"
					icon={CrossIcon}
					label={formatMessage(i18n.bannerDismissLabel)}
					onClick={handleDismiss}
				/>
			</Box>
		</div>
	);
};

const CwrWelcomeBannerInner = ({ contentId, isLivePage }: CwrWelcomeBannerProps) => {
	const cwrWelcomeParam = useCwrWelcomeParam();

	const isValidBannerRoute = useRouteName({
		selector: (routeName: string | undefined) =>
			routeName === VIEW_PAGE.name ||
			routeName === VIEW_BLOG.name ||
			routeName === VIEW_BLOG_DATE_LEGACY.name,
	});

	const variant: CwrWelcomeVariant | null = isCwrWelcomeVariant(cwrWelcomeParam)
		? cwrWelcomeParam
		: null;

	const shouldRenderBanner = isValidBannerRoute && !!contentId && variant !== null;

	if (!shouldRenderBanner) {
		return null;
	}

	return <CwrWelcomeBannerContent isLivePage={isLivePage} variant={variant} />;
};

export const CwrWelcomeBanner = ({
	contentId,
	isLivePage,
}: CwrWelcomeBannerProps): React.JSX.Element => (
	<ErrorBoundary attribution={Attribution.FLYWHEEL_ENGAGEMENT}>
		<CwrWelcomeBannerInner contentId={contentId} isLivePage={isLivePage} />
	</ErrorBoundary>
);
